/**
 * Saying, before the save and not after it, that this save opens the next version (R4).
 *
 * Editing an approved survey does not rewrite what is on the air: it opens version N+1 in draft,
 * and the version already live keeps being delivered until that draft is approved. The screen looks
 * exactly the same either way, so somebody changes one letter, presses save, and finds out on their
 * own that they are now on v2 waiting for a reviewer.
 *
 * **The behaviour is right and is not touched here.** The approved text is both what is being
 * delivered and what somebody reviewed; editing it in place would serve text nobody approved. What
 * was missing was saying so.
 *
 * It is a confirmation rather than a banner because the neighbouring path already asks: a change
 * with answers under it opens {@link SurveyVersionDialog}. Whoever learned to expect a question
 * there reads the silence here as "nothing is going to happen".
 *
 * Three things, in the order somebody needs them: which version opens, that it reaches nobody until
 * it is approved, and that the live one carries on in the meantime.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTModal } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-bottom: var(--space-s);
`;

const noticeStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-info);
  border-left: 3px solid var(--color-info);
  border-radius: var(--radius-card);
  background: var(--color-info-subtle);
  font-size: var(--font-size-sm);
  text-wrap: pretty;
`;

const keepsStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  font-size: var(--font-size-sm);
  text-wrap: pretty;
`;

const headlineStyle = css`
  margin: 0;
  font-weight: var(--font-weight-semibold);
`;

const lineStyle = css`
  margin: 0;
`;

export interface SurveyDraftVersionDialogProps {
  /** The version being delivered right now — the one that carries on. */
  liveVersion: number;
  isWorking: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export const SurveyDraftVersionDialog = ({
  liveVersion,
  isWorking,
  onConfirm,
  onClose,
}: SurveyDraftVersionDialogProps): JSX.Element => {
  const draftVersion = liveVersion + 1;

  return (
    <UTModal
      open
      position='center'
      maxWidth='560px'
      hasButtonClose
      modalContentId='survey-draft-version-dialog'
      closeButtonId='survey-draft-version-dialog-close'
      title={`Salvar abre a versão ${draftVersion}`}
      description='Esta pesquisa está aprovada, e o texto aprovado é o que está no ar. Salvar não o reescreve.'
      content={
        <div className={contentStyle}>
          <div className={noticeStyle}>
            <p className={headlineStyle}>
              As suas mudanças vão para a versão {draftVersion}, em rascunho.
            </p>
            <p className={lineStyle}>
              Ela <strong>não é entregue a ninguém</strong> enquanto não for aprovada — depois de
              salvar, ela ainda precisa ser enviada para aprovação e aprovada para entrar no ar.
            </p>
          </div>

          <div className={keepsStyle}>
            <p className={headlineStyle}>O que continua no ar</p>
            <p className={lineStyle}>
              A versão {liveVersion} segue sendo entregue e recebendo respostas até a versão{' '}
              {draftVersion} ser aprovada. As respostas dela ficam guardadas, e os resultados das
              duas versões não se misturam.
            </p>
          </div>
        </div>
      }
      customButtons={[
        { text: 'Cancelar', variant: 'secondary', disabled: isWorking, onClick: onClose },
        {
          text: `Salvar e abrir a versão ${draftVersion}`,
          loading: isWorking,
          disabled: isWorking,
          onClick: onConfirm,
        },
      ]}
      onClose={onClose}
    />
  );
};
